import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardList, Play, Eye, Trash2, RefreshCw, Filter, AlertTriangle, CheckCircle, Clock, Package, Loader2, TrendingUp, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const DATE_PRESETS = [
  { label: 'Yesterday', key: 'yesterday' },
  { label: 'Today', key: 'today' },
  { label: 'Weekly', key: 'weekly' },
  { label: 'Monthly', key: 'monthly' },
  { label: 'Custom', key: 'custom' },
];

const getDateRange = (preset) => {
  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];

  switch (preset) {
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { start: fmt(y), end: fmt(y) };
    }
    case 'today':
      return { start: fmt(today), end: fmt(today) };
    case 'weekly': {
      const w = new Date(today);
      w.setDate(w.getDate() - 6);
      return { start: fmt(w), end: fmt(today) };
    }
    case 'monthly': {
      const m = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: fmt(m), end: fmt(today) };
    }
    default:
      return { start: '', end: '' };
  }
};

const PRIORITY_CONFIG = {
  Highest: { color: 'bg-red-100 text-red-700 border-red-300', dotColor: 'bg-red-500' },
  High: { color: 'bg-orange-100 text-orange-700 border-orange-300', dotColor: 'bg-orange-500' },
  Normal: { color: 'bg-gray-100 text-gray-600 border-gray-300', dotColor: 'bg-gray-400' },
  Low: { color: 'bg-blue-100 text-blue-600 border-blue-300', dotColor: 'bg-blue-400' },
};

const PickSlips = () => {
  const navigate = useNavigate();
  const [pickSlips, setPickSlips] = useState([]);
  const [pendingSlips, setPendingSlips] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [summary, setSummary] = useState({ total_pick_qty: 0, in_progress_qty: 0, completed_qty: 0, pending_qty: 0, cancelled_qty: 0, opening_pending_qty: 0, closing_pending_qty: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [triggering, setTriggering] = useState(false);
  const [datePreset, setDatePreset] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);

  // Assign picker
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignSlipNo, setAssignSlipNo] = useState(null);
  const [sfgPickers, setSfgPickers] = useState([]);
  const [loadingPickers, setLoadingPickers] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  // Return-to-SFG (re-tag bundle with NEW code)
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnTarget, setReturnTarget] = useState(null); // { bundle_code, slip_no }
  const [returnNewBundle, setReturnNewBundle] = useState('');
  const [returnBin, setReturnBin] = useState('');
  const [returnBinSuggestion, setReturnBinSuggestion] = useState(null);
  const [returnBinLoading, setReturnBinLoading] = useState(false);
  const [returnStaffId, setReturnStaffId] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  // 2026-05-24 — Per-category SFG picker pool. The slip's `trigger_type`
  // decides whether the Dhoti or Combo pool is loaded into the assign
  // dialog. Mapping mirrors the backend `_picker_category_for_trigger`.
  const [assignCategory, setAssignCategory] = useState('dhoti'); // 'dhoti' | 'combo'

  const triggerToCategory = (triggerType) => {
    const t = (triggerType || '').toUpperCase();
    if (t === 'COMBO_V2_BULK' || t === 'COMBO_ASSEMBLY') return 'combo';
    return 'dhoti';
  };

  const fetchSfgPickers = useCallback(async (category = 'dhoti') => {
    setLoadingPickers(true);
    try {
      const resp = await apiClient.get('/wms/sfg-pickers', { params: { category } });
      setSfgPickers(resp.data.pickers || []);
    } catch (err) {
      console.error('Failed to load SFG pickers:', err);
    } finally {
      setLoadingPickers(false);
    }
  }, []);

  const handleAssignPicker = async (staffId) => {
    if (!assignSlipNo) return;
    setAssigning(true);
    try {
      const resp = await apiClient.post(`/wms/pick-slips/${assignSlipNo}/assign`, { staff_id: staffId });
      toast.success(resp.data.message);
      setAssignDialogOpen(false);
      setAssignSlipNo(null);
      setPickerSearch('');
      fetchPickSlips();
      fetchPendingSlips();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign picker');
    } finally {
      setAssigning(false);
    }
  };

  const openAssignDialog = (slipNo, triggerType) => {
    const category = triggerToCategory(triggerType);
    setAssignSlipNo(slipNo);
    setAssignCategory(category);
    setAssignDialogOpen(true);
    setPickerSearch('');
    // Always reload pool so the dialog shows the right pickers for THIS slip's category
    fetchSfgPickers(category);
  };

  const handlePriorityChange = async (slipNo, newPriority) => {
    try {
      await apiClient.put(`/wms/pick-slips/${slipNo}/priority`, { priority: newPriority });
      toast.success(`Priority updated to ${newPriority}`);
      setPickSlips(prev => prev.map(s => 
        s.slip_no === slipNo ? { ...s, priority: newPriority } : s
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update priority');
    }
  };

  const fetchPendingSlips = useCallback(async () => {
    try {
      setPendingLoading(true);
      const response = await apiClient.get('/wms/pick-slips?status=PENDING');
      setPendingSlips(response.data.pick_slips || []);
    } catch (error) {
      console.error('Failed to fetch pending slips:', error);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingSlips();
  }, [fetchPendingSlips]);

  useEffect(() => {
    fetchPickSlips();
  }, [statusFilter, datePreset, customStart, customEnd]);

  const fetchPickSlips = async () => {
    try {
      setLoading(true);
      const params = [];
      if (statusFilter !== 'all') params.push(`status=${statusFilter}`);

      // Date range
      let range;
      if (datePreset === 'custom') {
        range = { start: customStart, end: customEnd };
      } else {
        range = getDateRange(datePreset);
      }
      if (range.start) params.push(`start_date=${range.start}`);
      if (range.end) params.push(`end_date=${range.end}`);

      const queryString = params.length > 0 ? '?' + params.join('&') : '';
      const response = await apiClient.get(`/wms/pick-slips${queryString}`);
      setPickSlips(response.data.pick_slips || []);
      setSummary(response.data.summary || { total_pick_qty: 0, in_progress_qty: 0, completed_qty: 0, pending_qty: 0, cancelled_qty: 0, opening_pending_qty: 0, closing_pending_qty: 0 });
    } catch (error) {
      toast.error('Failed to fetch pick slips');
    } finally {
      setLoading(false);
    }
  };

  const handleManualTrigger = async () => {
    try {
      setTriggering(true);
      const response = await apiClient.post('/wms/pick-slips/trigger-manual');
      toast.success(response.data.message);
      fetchPickSlips();
      fetchPendingSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger replenishment');
    } finally {
      setTriggering(false);
    }
  };

  // 2026-06-10 — Per-bundle PP (pieces-processing) handover-staff reassign.
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState(null); // {slip_no, bundle_code, current_name}
  const [presentPpStaff, setPresentPpStaff] = useState([]);
  const [presentPpLoading, setPresentPpLoading] = useState(false);
  const [reassignSearch, setReassignSearch] = useState('');
  const [reassigning, setReassigning] = useState(false);

  const fetchPresentPpStaff = useCallback(async () => {
    setPresentPpLoading(true);
    try {
      const resp = await apiClient.get('/wms/pick-slips/present-pp-staff');
      setPresentPpStaff(resp.data.staff || []);
    } catch (e) {
      console.error('Failed to load present PP staff:', e);
    } finally {
      setPresentPpLoading(false);
    }
  }, []);

  const openReassignDialog = (slipNo, bundleCode, currentName) => {
    setReassignTarget({ slip_no: slipNo, bundle_code: bundleCode, current_name: currentName });
    setReassignSearch('');
    setReassignDialogOpen(true);
    fetchPresentPpStaff();
  };

  const handleReassignPpStaff = async (staffId) => {
    if (!reassignTarget) return;
    setReassigning(true);
    try {
      const { slip_no, bundle_code } = reassignTarget;
      const resp = await apiClient.post(
        `/wms/pick-slips/${encodeURIComponent(slip_no)}/reassign-pp-staff`,
        { bundle_code, new_staff_id: staffId }
      );
      toast.success(`Reassigned to ${resp.data.to_staff_name || staffId}`);
      setReassignDialogOpen(false);
      setReassignTarget(null);
      handleViewDetail(slip_no); // refresh modal with new staff name
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reassign PP staff');
    } finally {
      setReassigning(false);
    }
  };

  const handleViewDetail = async (slipNo) => {
    try {
      const response = await apiClient.get(`/wms/pick-slips/${slipNo}`);
      setSelectedSlip(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch slip details');
    }
  };

  const handleDeleteSlip = async (slipNo, status) => {
    const isCompleted = status === 'COMPLETED';
    const msg = isCompleted
      ? `This is a COMPLETED slip. Deleting it will revert picked bundles back to SFG storage.\n\nDelete pick slip ${slipNo}?`
      : `Delete pick slip ${slipNo}? Allocated bundles will be released back to storage.`;
    if (!window.confirm(msg)) return;
    try {
      await apiClient.delete(`/wms/pick-slips/${slipNo}`, { params: { force: isCompleted } });
      toast.success(`Pick slip ${slipNo} deleted`);
      fetchPickSlips();
      fetchPendingSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete slip');
    }
  };

  const handleStartPicking = (slipNo) => {
    navigate(`/wms/picker-guidance?slip=${slipNo}`);
  };

  // Force-complete a stuck IN_PROGRESS slip: skips remaining un-picked bundles
  // and marks it completed so it stops being handed back to the picker
  // ("Continuing existing pick slip" loop).
  const handleForceComplete = async (slipNo) => {
    if (!window.confirm(
      `Force-complete pick slip ${slipNo}?\n\nAny remaining un-picked bundles will be skipped and the slip marked completed, so it stops being handed back to the picker.`
    )) return;
    try {
      const res = await apiClient.get(`/wms/pick-slips/${encodeURIComponent(slipNo)}/force-complete`);
      toast.success(res.data?.message || `Slip ${slipNo} completed`);
      fetchPickSlips();
      fetchPendingSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to force-complete slip');
    }
  };

  // Open the Return-to-SFG dialog with pre-filled context.
  const openReturnDialog = async (bundleCode, slipNo) => {
    setReturnTarget({ bundle_code: bundleCode, slip_no: slipNo });
    setReturnNewBundle('');
    setReturnBin('');
    setReturnBinSuggestion(null);
    setReturnStaffId('');
    setReturnReason('');
    setReturnDialogOpen(true);
    // Auto-suggest a bin based on the bundle's SKU
    setReturnBinLoading(true);
    try {
      const resp = await apiClient.get('/wms/sfg/suggest-return-bin', {
        params: { old_bundle_code: bundleCode },
      });
      if (resp.data?.success && resp.data?.suggested_bin?.bin_code) {
        const sb = resp.data.suggested_bin;
        setReturnBin(sb.bin_code);
        setReturnBinSuggestion(sb);
      } else {
        setReturnBinSuggestion({ reason: resp.data?.message || 'No bin could be suggested' });
      }
    } catch (err) {
      setReturnBinSuggestion({ reason: err.response?.data?.detail || 'Failed to fetch bin suggestion' });
    } finally {
      setReturnBinLoading(false);
    }
  };

  const handleSubmitReturnToSfg = async () => {
    if (!returnTarget) return;
    const newCode = returnNewBundle.trim();
    const bin = returnBin.trim();
    const staffId = returnStaffId.trim();
    if (!newCode) {
      toast.error('Enter a NEW bundle code');
      return;
    }
    if (newCode === returnTarget.bundle_code) {
      toast.error('New bundle code must differ from the old one');
      return;
    }
    if (!bin) {
      toast.error('Enter the bin location to store the returned bundle');
      return;
    }
    if (!staffId) {
      toast.error('Enter the staff ID performing the return');
      return;
    }
    setReturnSubmitting(true);
    try {
      const resp = await apiClient.post('/wms/sfg/return-bundle-to-sfg', {
        old_bundle_code: returnTarget.bundle_code,
        new_bundle_code: newCode,
        staff_id: staffId,
        bin_location: bin,
        pick_slip_no: returnTarget.slip_no,
        reason: returnReason.trim() || null,
      });
      toast.success(resp.data?.message || `Bundle returned as ${newCode}`);
      setReturnDialogOpen(false);
      setDetailDialogOpen(false);
      // Refresh slip list so the bundle table picks up the retag.
      fetchPickSlips();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to return bundle to SFG');
    } finally {
      setReturnSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      PENDING: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      ASSIGNED: { color: 'bg-indigo-100 text-indigo-700', icon: Play },
      IN_PROGRESS: { color: 'bg-blue-100 text-blue-700', icon: Play },
      COMPLETED: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      COMPLETED_WITH_SKIPS: { color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
      CANCELLED: { color: 'bg-gray-100 text-gray-500', icon: null }
    };
    const { color, icon: Icon } = config[status] || config.PENDING;
    return (
      <Badge className={`${color} font-medium`}>
        {Icon && <Icon className="w-3 h-3 mr-1" />}
        {status}
      </Badge>
    );
  };

  // Universal slip matcher — slip no / SKU / design / picker / bundle codes
  const slipMatchesSearch = useCallback((slip, term) => {
    if (!term) return true;
    const q = term.toLowerCase();
    return (
      slip.slip_no?.toLowerCase().includes(q) ||
      slip.sku_code?.toLowerCase().includes(q) ||
      (slip.design_name && slip.design_name.toLowerCase().includes(q)) ||
      (slip.picker_name && slip.picker_name.toLowerCase().includes(q)) ||
      (Array.isArray(slip.bundles) && slip.bundles.some(
        (b) => b.bundle_code && b.bundle_code.toLowerCase().includes(q)
      ))
    );
  }, []);

  const filteredPendingSlips = useMemo(
    () => pendingSlips.filter((slip) => slipMatchesSearch(slip, searchTerm)),
    [pendingSlips, searchTerm, slipMatchesSearch]
  );

  // For the main table, exclude PENDING slips (shown in dedicated section) unless user explicitly filters by PENDING
  const filteredSlips = useMemo(() => {
    const slipsToShow = statusFilter === 'PENDING' ? pickSlips : pickSlips.filter(s => s.status !== 'PENDING');
    return slipsToShow.filter((slip) => slipMatchesSearch(slip, searchTerm));
  }, [pickSlips, searchTerm, statusFilter, slipMatchesSearch]);

  const tiles = [
    { label: 'Opening Pending', value: summary.opening_pending_qty, color: 'bg-purple-600', icon: Clock },
    { label: 'Pick Slip Qty', value: summary.total_pick_qty, color: 'bg-blue-600', icon: Package },
    { label: 'In-Progress Qty', value: summary.in_progress_qty, color: 'bg-amber-500', icon: TrendingUp },
    { label: 'Completed Qty', value: summary.completed_qty, color: 'bg-emerald-600', icon: CheckCircle },
    { label: 'Closing Pending', value: summary.closing_pending_qty, color: 'bg-orange-500', icon: Clock },
  ];

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Pick Slips</h1>
              <p className="text-sm text-[#E8DCC8]">Manage replenishment pick slips</p>
            </div>
          </div>
          <Button
            onClick={handleManualTrigger}
            disabled={triggering}
            className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
            data-testid="manual-trigger-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${triggering ? 'animate-spin' : ''}`} />
            Trigger Replenishment
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Date Filter Buttons */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="date-filter-bar">
          {DATE_PRESETS.map(p => (
            <Button
              key={p.key}
              size="sm"
              variant={datePreset === p.key ? 'default' : 'outline'}
              onClick={() => setDatePreset(p.key)}
              className={datePreset === p.key
                ? 'bg-[#1A1F2E] text-white hover:bg-[#2a2f3e]'
                : 'border-[#E5DFD0] text-[#5A5F6E] hover:bg-gray-50'}
              data-testid={`date-filter-${p.key}`}
            >
              {p.label}
            </Button>
          ))}
          {datePreset === 'custom' && (
            <div className="flex items-center gap-2 ml-2">
              <Input
                type="date"
                value={customStart}
                onChange={e => setCustomStart(e.target.value)}
                className="w-36 h-8 text-sm"
                data-testid="custom-start-date"
              />
              <span className="text-[#5A5F6E] text-sm">to</span>
              <Input
                type="date"
                value={customEnd}
                onChange={e => setCustomEnd(e.target.value)}
                className="w-36 h-8 text-sm"
                data-testid="custom-end-date"
              />
            </div>
          )}
        </div>

        {/* Summary Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="summary-tiles">
          {tiles.map(tile => (
            <Card key={tile.label} className="border-[#E5DFD0] overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-center">
                  <div className={`${tile.color} w-14 h-full flex items-center justify-center self-stretch`}>
                    <tile.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-xs text-[#5A5F6E] font-medium">{tile.label}</p>
                    <p className="text-2xl font-bold text-[#1A1F2E]" data-testid={`tile-${tile.label.toLowerCase().replace(/\s/g, '-')}`}>
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : tile.value.toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[#5A5F6E]" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="ASSIGNED">Assigned</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="COMPLETED_WITH_SKIPS">Completed (Skips)</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by slip no, SKU, design, or bundle no"
                className="max-w-md"
              />
            </div>
            <div className="text-sm text-[#5A5F6E]">
              {filteredSlips.length} slip(s)
            </div>
          </div>
        </div>

        {/* All Pending Slips Section — always visible regardless of date filter */}
        {statusFilter !== 'PENDING' && (
          <div className="bg-white rounded-lg border-2 border-yellow-300 overflow-hidden mb-6" data-testid="pending-slips-section">
            <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-yellow-600" />
                <h2 className="font-semibold text-yellow-800 text-sm">All Pending Slips</h2>
                <Badge className="bg-yellow-200 text-yellow-800 text-xs">{filteredPendingSlips.length}</Badge>
              </div>
              <p className="text-xs text-yellow-600">Shown regardless of date filter</p>
            </div>
            {pendingLoading ? (
              <div className="p-6 text-center text-[#5A5F6E]">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" />
                <p className="text-sm">Loading pending slips...</p>
              </div>
            ) : filteredPendingSlips.length === 0 ? (
              <div className="p-6 text-center">
                <CheckCircle className="w-8 h-8 text-green-300 mx-auto mb-2" />
                <p className="text-sm text-[#5A5F6E]">No pending slips</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-yellow-50/50 border-b border-yellow-100">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-[#1A1F2E]">Slip No</th>
                      <th className="px-4 py-2 text-left font-medium text-[#1A1F2E]">SKU</th>
                      <th className="px-4 py-2 text-left font-medium text-[#1A1F2E]">Design</th>
                      <th className="px-4 py-2 text-right font-medium text-[#1A1F2E]">Pick Qty</th>
                      <th className="px-4 py-2 text-center font-medium text-[#1A1F2E]">Bundles</th>
                      <th className="px-4 py-2 text-center font-medium text-[#1A1F2E]">Type</th>
                      <th className="px-4 py-2 text-center font-medium text-[#1A1F2E]">Priority</th>
                      <th className="px-4 py-2 text-left font-medium text-[#1A1F2E]">Picker</th>
                      <th className="px-4 py-2 text-left font-medium text-[#1A1F2E]">Triggered</th>
                      <th className="px-4 py-2 text-center font-medium text-[#1A1F2E]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPendingSlips.map((slip) => (
                      <tr key={slip.slip_id} className="border-b border-yellow-100 hover:bg-yellow-50/30">
                        <td className="px-4 py-2 font-mono font-medium text-[#1A1F2E]">
                          {slip.slip_no}
                          {slip.is_partial && (
                            <Badge className="ml-2 bg-orange-100 text-orange-700 text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Partial
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-2 text-[#1A1F2E]">{slip.sku_code}</td>
                        <td className="px-4 py-2 text-[#5A5F6E]">{slip.design_name || '-'}</td>
                        <td className="px-4 py-2 text-right font-medium">{slip.pick_qty?.toLocaleString()}</td>
                        <td className="px-4 py-2 text-center">{slip.total_bundles}</td>
                        <td className="px-4 py-2 text-center">
                          <Badge className={slip.trigger_type === 'AUTO' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}>
                            {slip.trigger_type}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <Select
                            value={slip.priority || 'Normal'}
                            onValueChange={(val) => {
                              handlePriorityChange(slip.slip_no, val);
                              fetchPendingSlips();
                            }}
                          >
                            <SelectTrigger className={`w-28 h-7 text-xs border ${PRIORITY_CONFIG[slip.priority || 'Normal']?.color || 'bg-gray-100 text-gray-600'}`} data-testid={`pending-priority-${slip.slip_no}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Highest"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>Highest</span></SelectItem>
                              <SelectItem value="High"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-500 inline-block"></span>High</span></SelectItem>
                              <SelectItem value="Normal"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-400 inline-block"></span>Normal</span></SelectItem>
                              <SelectItem value="Low"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block"></span>Low</span></SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-2 text-[#5A5F6E] text-sm">
                          <div className="flex items-center gap-1">
                            <span className="truncate">{slip.picker_name || slip.picked_by || '-'}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openAssignDialog(slip.slip_no, slip.trigger_type)}
                              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 h-6 w-6 p-0 flex-shrink-0"
                              title={slip.picker_name ? 'Reassign' : 'Assign picker'}
                              data-testid={`pending-assign-picker-${slip.slip_no}`}
                            >
                              <UserPlus className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-[#5A5F6E] text-sm">
                          {new Date(slip.triggered_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center justify-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleViewDetail(slip.slip_no)} className="text-blue-600" data-testid={`pending-view-${slip.slip_no}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleStartPicking(slip.slip_no)} className="text-green-600" data-testid={`pending-start-${slip.slip_no}`}>
                              <Play className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteSlip(slip.slip_no, slip.status)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`pending-delete-${slip.slip_no}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Pick Slips Table */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#5A5F6E]">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading...
            </div>
          ) : filteredSlips.length === 0 ? (
            <div className="p-12 text-center">
              <ClipboardList className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
              <p className="text-[#5A5F6E]">No pick slips found for selected period.</p>
              <Button
                variant="link"
                onClick={handleManualTrigger}
                className="text-[#2D5F4F] mt-2"
              >
                Trigger replenishment check
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-[#E5DFD0]">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Slip No</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">SKU</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Design</th>
                    <th className="px-4 py-3 text-right font-medium text-[#1A1F2E]">Pick Qty</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Bundles</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Type</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Status</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Priority</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Picker</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Triggered</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSlips.map((slip) => (
                    <tr key={slip.slip_id} className="border-b border-[#E5DFD0] hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono font-medium text-[#1A1F2E]">
                        {slip.slip_no}
                        {slip.is_partial && (
                          <Badge className="ml-2 bg-orange-100 text-orange-700 text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Partial
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#1A1F2E]">{slip.sku_code}</td>
                      <td className="px-4 py-3 text-[#5A5F6E]">{slip.design_name || '-'}</td>
                      <td className="px-4 py-3 text-right font-medium">{slip.pick_qty?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-center">{slip.total_bundles}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={slip.trigger_type === 'AUTO' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}>
                          {slip.trigger_type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(slip.status)}</td>
                      <td className="px-4 py-3 text-center">
                        {slip.status === 'PENDING' ? (
                          <Select
                            value={slip.priority || 'Normal'}
                            onValueChange={(val) => handlePriorityChange(slip.slip_no, val)}
                          >
                            <SelectTrigger className={`w-28 h-7 text-xs border ${PRIORITY_CONFIG[slip.priority || 'Normal']?.color || 'bg-gray-100 text-gray-600'}`} data-testid={`priority-${slip.slip_no}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Highest"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>Highest</span></SelectItem>
                              <SelectItem value="High"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-500 inline-block"></span>High</span></SelectItem>
                              <SelectItem value="Normal"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-400 inline-block"></span>Normal</span></SelectItem>
                              <SelectItem value="Low"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block"></span>Low</span></SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_CONFIG[slip.priority || 'Normal']?.color || 'bg-gray-100 text-gray-600'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_CONFIG[slip.priority || 'Normal']?.dotColor || 'bg-gray-400'}`}></span>
                            {slip.priority || 'Normal'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#5A5F6E] text-sm">
                        <div className="flex items-center gap-1">
                          <span className="truncate">{slip.picker_name || slip.picked_by || '-'}</span>
                          {slip.status !== 'COMPLETED' && slip.status !== 'COMPLETED_WITH_SKIPS' && slip.status !== 'CANCELLED' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openAssignDialog(slip.slip_no, slip.trigger_type)}
                              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 h-6 w-6 p-0 flex-shrink-0"
                              title={slip.picker_name ? 'Reassign' : 'Assign picker'}
                              data-testid={`assign-picker-${slip.slip_no}`}
                            >
                              <UserPlus className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#5A5F6E] text-sm">
                        {new Date(slip.triggered_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetail(slip.slip_no)}
                            className="text-blue-600"
                            data-testid={`view-slip-${slip.slip_no}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {(slip.status === 'PENDING' || slip.status === 'IN_PROGRESS') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleStartPicking(slip.slip_no)}
                              className="text-green-600"
                              data-testid={`start-picking-${slip.slip_no}`}
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                          )}
                          {slip.status === 'IN_PROGRESS' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleForceComplete(slip.slip_no)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              data-testid={`force-complete-${slip.slip_no}`}
                              title="Force complete (skip remaining bundles) — clears stuck slips"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {slip.status !== 'CANCELLED' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteSlip(slip.slip_no, slip.status)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-slip-${slip.slip_no}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pick Slip Details - {selectedSlip?.slip_no}</DialogTitle>
          </DialogHeader>
          {selectedSlip && (
            <div className="py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-[#5A5F6E]">Target Month</p>
                  <p className="font-medium">{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][selectedSlip.target_month - 1]} {selectedSlip.target_year}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">Projection</p>
                  <p className="font-medium">{selectedSlip.target_month_projection?.toLocaleString()} units</p>
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">FG Stock at Trigger</p>
                  <p className="font-medium">{selectedSlip.fg_stock_at_trigger?.toLocaleString()} units</p>
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">Required Qty</p>
                  <p className="font-medium">{selectedSlip.required_qty?.toLocaleString()} units</p>
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">Pick Qty (Rounded)</p>
                  <p className="font-medium text-green-700">{selectedSlip.pick_qty?.toLocaleString()} units</p>
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">Min Case Pack</p>
                  <p className="font-medium">{selectedSlip.min_case_pack_size} units</p>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-[#1A1F2E] mb-2">Bundles to Pick ({selectedSlip.bundles?.length})</h3>
                <div className="max-h-60 overflow-y-auto border border-[#E5DFD0] rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-left">Bundle</th>
                        <th className="px-3 py-2 text-left">Bin</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-center">Status</th>
                        <th className="px-3 py-2 text-left">PP Staff</th>
                        <th className="px-3 py-2 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSlip.bundles?.map((bundle) => (
                        <tr key={bundle.bundle_code} className="border-t border-[#E5DFD0]">
                          <td className="px-3 py-2">{bundle.pick_sequence}</td>
                          <td className="px-3 py-2 font-mono">{bundle.bundle_code}</td>
                          <td className="px-3 py-2">{bundle.bin_location}</td>
                          <td className="px-3 py-2 text-right">{bundle.usn_count}</td>
                          <td className="px-3 py-2 text-center">
                            {bundle.picked ? (
                              <CheckCircle className="w-4 h-4 text-green-600 mx-auto" />
                            ) : (
                              <Clock className="w-4 h-4 text-yellow-600 mx-auto" />
                            )}
                          </td>
                          <td className="px-3 py-2" data-testid={`pp-staff-cell-${bundle.bundle_code}`}>
                            {bundle.pp_staff_name ? (
                              <div className="flex items-center gap-1.5">
                                <span className="text-[#1A1F2E]">{bundle.pp_staff_name}</span>
                                {bundle.pp_can_reassign ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-1.5 text-[10px] text-blue-600 hover:bg-blue-50"
                                    onClick={() => openReassignDialog(selectedSlip.slip_no, bundle.bundle_code, bundle.pp_staff_name)}
                                    title="Reassign PP staff (absent)"
                                    data-testid={`reassign-pp-btn-${bundle.bundle_code}`}
                                  >
                                    <UserPlus className="w-3 h-3 mr-0.5" /> Reassign
                                  </Button>
                                ) : (
                                  <span className="text-[10px] text-gray-400" title="Labels already printed — reassign locked">🔒</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-[10px] px-2 border-orange-400 text-orange-700 hover:bg-orange-50"
                              onClick={() => openReturnDialog(bundle.bundle_code, selectedSlip.slip_no)}
                              data-testid={`return-to-sfg-btn-${bundle.bundle_code}`}
                            >
                              Return to SFG
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
            {selectedSlip?.status === 'PENDING' && (
              <Button
                onClick={() => {
                  setDetailDialogOpen(false);
                  handleStartPicking(selectedSlip.slip_no);
                }}
                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              >
                <Play className="w-4 h-4 mr-2" />
                Start Picking
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Picker Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="assign-picker-dialog">
          <DialogHeader>
            <DialogTitle>
              Assign {assignCategory === 'combo' ? 'Combo' : 'Dhoti'} Picker
            </DialogTitle>
            <DialogDescription>
              Select a {assignCategory === 'combo' ? 'Combo' : 'Dhoti'} SFG picker for slip{' '}
              <span className="font-mono font-bold">{assignSlipNo}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Search picker..."
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid="picker-search-input"
            />
            <div className="max-h-60 overflow-y-auto space-y-1">
              {loadingPickers ? (
                <p className="text-center text-gray-400 text-sm py-4">Loading pickers...</p>
              ) : sfgPickers.filter(p => {
                if (!pickerSearch.trim()) return true;
                const q = pickerSearch.toLowerCase();
                return p.name.toLowerCase().includes(q) || p.staff_id.toLowerCase().includes(q);
              }).length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-4">
                  No {assignCategory === 'combo' ? 'Combo' : 'Dhoti'} pickers set for today. Configure them in Dispatch Supervisor.
                </p>
              ) : (
                sfgPickers.filter(p => {
                  if (!pickerSearch.trim()) return true;
                  const q = pickerSearch.toLowerCase();
                  return p.name.toLowerCase().includes(q) || p.staff_id.toLowerCase().includes(q);
                }).map((picker) => (
                  <button
                    key={picker.staff_id}
                    onClick={() => handleAssignPicker(picker.staff_id)}
                    disabled={assigning}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-indigo-50 border border-transparent hover:border-indigo-200 transition-all text-left"
                    data-testid={`picker-option-${picker.staff_id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{picker.name}</p>
                      <p className="text-xs text-gray-400">{picker.staff_id}{picker.designation ? ` · ${picker.designation}` : ''}</p>
                    </div>
                    <UserPlus className="w-4 h-4 text-indigo-400" />
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reassign PP (pieces-processing) Staff Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="reassign-pp-dialog">
          <DialogHeader>
            <DialogTitle>Reassign Pieces-Processing Staff</DialogTitle>
            <DialogDescription>
              Bundle <span className="font-mono font-bold">{reassignTarget?.bundle_code}</span>
              {reassignTarget?.current_name ? <> · currently <span className="font-medium">{reassignTarget.current_name}</span></> : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Search present PP staff..."
              value={reassignSearch}
              onChange={(e) => setReassignSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid="reassign-pp-search-input"
            />
            <div className="max-h-60 overflow-y-auto space-y-1">
              {presentPpLoading ? (
                <p className="text-center text-gray-400 text-sm py-4">Loading staff...</p>
              ) : presentPpStaff.filter(p => {
                if (!reassignSearch.trim()) return true;
                const q = reassignSearch.toLowerCase();
                return (p.staff_name || '').toLowerCase().includes(q) || (p.staff_id || '').toLowerCase().includes(q);
              }).length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-4">
                  No pieces-processing staff marked present today. Set attendance first.
                </p>
              ) : (
                presentPpStaff.filter(p => {
                  if (!reassignSearch.trim()) return true;
                  const q = reassignSearch.toLowerCase();
                  return (p.staff_name || '').toLowerCase().includes(q) || (p.staff_id || '').toLowerCase().includes(q);
                }).map((staff) => (
                  <button
                    key={staff.staff_id}
                    onClick={() => handleReassignPpStaff(staff.staff_id)}
                    disabled={reassigning}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-emerald-50 border border-transparent hover:border-emerald-200 transition-all text-left"
                    data-testid={`reassign-pp-option-${staff.staff_id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{staff.staff_name}</p>
                      <p className="text-xs text-gray-400">{staff.staff_id}</p>
                    </div>
                    <Badge variant="outline" className={staff.pool === 'combo' ? 'text-fuchsia-600 border-fuchsia-300' : 'text-indigo-600 border-indigo-300'}>
                      {staff.pool === 'combo' ? 'Combo' : 'Regular'}
                    </Badge>
                  </button>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Return-to-SFG Dialog */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent className="max-w-md" data-testid="return-to-sfg-dialog">
          <DialogHeader>
            <DialogTitle>Return Bundle to SFG</DialogTitle>
            <DialogDescription>
              Returning bundle <span className="font-mono font-bold">{returnTarget?.bundle_code}</span>
              {returnTarget?.slip_no ? <> from slip <span className="font-mono">{returnTarget.slip_no}</span></> : null}.
              <span className="block mt-2 text-amber-700 text-xs">
                A bundle code cannot be put away twice. Enter a NEW code below — every USN
                attached to the old bundle will be re-tagged to the new code automatically.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">
                New Bundle Code <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="Scan or type the new bundle code"
                value={returnNewBundle}
                onChange={(e) => setReturnNewBundle(e.target.value.toUpperCase())}
                autoFocus
                className="font-mono"
                data-testid="return-new-bundle-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">
                Bin Location <span className="text-red-500">*</span>
                {returnBinLoading && (
                  <span className="ml-2 inline-flex items-center text-[11px] font-normal text-gray-500">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Suggesting…
                  </span>
                )}
                {!returnBinLoading && returnBinSuggestion?.bin_code && (
                  <span
                    className="ml-2 inline-block text-[11px] font-normal text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-[1px]"
                    data-testid="return-bin-suggestion-badge"
                    title={`Capacity: ${returnBinSuggestion.current_stock}/${returnBinSuggestion.capacity}`}
                  >
                    Suggested · {returnBinSuggestion.reason}
                  </span>
                )}
              </label>
              <Input
                placeholder="e.g. BIN-A1-12"
                value={returnBin}
                onChange={(e) => setReturnBin(e.target.value.toUpperCase())}
                className="font-mono"
                data-testid="return-bin-input"
              />
              {!returnBinLoading && returnBinSuggestion && !returnBinSuggestion.bin_code && (
                <p className="text-[11px] text-amber-700 mt-1">
                  {returnBinSuggestion.reason}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">
                Staff ID <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="e.g. ID No : S00123"
                value={returnStaffId}
                onChange={(e) => setReturnStaffId(e.target.value)}
                className="font-mono"
                data-testid="return-staff-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">
                Reason (optional)
              </label>
              <Input
                placeholder="e.g. Excess from pick / Wrong bundle / Damaged"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                data-testid="return-reason-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setReturnDialogOpen(false)}
              disabled={returnSubmitting}
              data-testid="return-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitReturnToSfg}
              disabled={returnSubmitting || !returnNewBundle.trim() || !returnBin.trim() || !returnStaffId.trim()}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="return-submit-btn"
            >
              {returnSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Re-tagging…
                </>
              ) : (
                'Confirm Return & Re-tag USNs'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PickSlips;
